import { memo } from 'react';
import clsx from 'clsx';
import { Badge } from '../../components/common/Badge';

interface WorkspacePanelProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  /** Render as an operational panel with header-strip instead of card padding */
  operational?: boolean;
  live?: boolean;
  actions?: React.ReactNode;
}

interface MetricCardProps {
  label: string;
  value: string;
  detail: string;
  tone?: 'default' | 'win' | 'loss' | 'warn' | 'info';
  /** Compact tactical display without description */
  compact?: boolean;
  live?: boolean;
}

interface MeterRowProps {
  label: string;
  valueLabel: string;
  share: number;
  tone?: 'brand' | 'win' | 'warn' | 'loss';
  detail?: string;
}

const toneClasses = {
  brand: 'bg-brand',
  win: 'bg-win',
  warn: 'bg-warn',
  loss: 'bg-loss',
};

export function WorkspacePanel({ title, description, children, className, operational = false, live = false, actions }: WorkspacePanelProps) {
  if (operational) {
    return (
      <section className={clsx('op-panel', className)}>
        <div className="op-panel-header">
          <div className="op-panel-title">
            {live && (
              <span className="relative flex h-1.5 w-1.5 mr-0.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-win opacity-60" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-win" />
              </span>
            )}
            {title}
          </div>
          {actions && <div className="flex items-center gap-1">{actions}</div>}
        </div>
        <div className="p-4 space-y-4">
          {description && <p className="text-xs text-gray-500">{description}</p>}
          {children}
        </div>
      </section>
    );
  }
  return (
    <section className={clsx('card p-5 space-y-4', className)}>
      <div className="space-y-1">
        <h2 className="text-sm font-semibold text-gray-200">{title}</h2>
        {description ? <p className="text-xs text-gray-500">{description}</p> : null}
      </div>
      {children}
    </section>
  );
}

export const MetricCard = memo(function MetricCard({ label, value, detail, tone = 'default', compact = false, live = false }: MetricCardProps) {
  if (compact) {
    return (
      <div className={clsx(
        'bg-surface-1 border rounded-lg p-3 space-y-1 transition-all duration-150',
        tone === 'win'  && 'border-win/25 hover:border-win/40',
        tone === 'loss' && 'border-loss/25 hover:border-loss/40',
        tone === 'warn' && 'border-warn/20 hover:border-warn/35',
        tone === 'info' && 'border-info/20 hover:border-info/35',
        (tone === 'default') && 'border-surface-4 hover:border-surface-3',
      )}>
        <div className="flex items-center justify-between">
          <span className="op-label">{label}</span>
          {live && (
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-win opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-win" />
            </span>
          )}
        </div>
        <div className={clsx(
          'text-xl font-bold font-mono tabular-nums',
          tone === 'win'  && 'text-win',
          tone === 'loss' && 'text-loss',
          tone === 'warn' && 'text-warn',
          tone === 'info' && 'text-info',
          tone === 'default' && 'text-gray-100',
        )}>
          {value}
        </div>
      </div>
    );
  }
  return (
    <div className="op-panel p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="stat-label">{label}</span>
        <Badge variant={tone} size="xs">
          {tone === 'default' ? 'Live' : tone === 'win' ? 'Strong' : tone === 'warn' ? 'Watch' : tone === 'info' ? 'System' : 'Risk'}
        </Badge>
      </div>
      <div className="text-2xl font-semibold font-mono text-gray-100">{value}</div>
      <p className="text-xs text-gray-500 leading-relaxed">{detail}</p>
    </div>
  );
});

export const MeterRow = memo(function MeterRow({ label, valueLabel, share, tone = 'brand', detail }: MeterRowProps) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="text-gray-300">{label}</span>
        <span className="font-mono text-gray-400">{valueLabel}</span>
      </div>
      <div className="h-1.5 rounded-full bg-surface-3 overflow-hidden">
        <div
          className={clsx('h-full rounded-full transition-all duration-500', toneClasses[tone])}
          style={{ width: `${Math.max(6, Math.min(100, share * 100))}%` }}
        />
      </div>
      {detail ? <p className="text-[11px] text-gray-600">{detail}</p> : null}
    </div>
  );
});
